import { createFileRoute, notFound } from "@tanstack/react-router";
import { ArticleView, type ArticleViewData } from "@/components/marketing/ArticleView";
import { VIEW_DICT_EN } from "@/lib/articles.dict";
import { getArticleByPath } from "@/lib/articles.functions";
import { renderSafeMarkdown } from "@/lib/safe-markdown";

const SITE_URL = "https://banana.cards";
const SITE_NAME = "Banana Cards";

export const Route = createFileRoute("/articles/$")({
  loader: async ({ params }) => {
    const splat = (params as any)._splat ?? "";
    const raw = await getArticleByPath({ data: { path: splat, locale: "en" } });
    if (!raw || raw.type === "none") throw notFound();

    if (raw.type === "article") {
      const bodyHtml = renderSafeMarkdown(raw.article.body_md ?? "");
      const view: ArticleViewData = {
        type: "article",
        locale: raw.locale,
        breadcrumbs: raw.breadcrumbs,
        article: { ...raw.article, bodyHtml },
        related: raw.related,
      };
      return { view, seo: raw };
    }
    const view: ArticleViewData = {
      type: "category",
      locale: raw.locale,
      breadcrumbs: raw.breadcrumbs,
      category: raw.category,
      subcategories: raw.subcategories,
      articles: raw.articles,
    };
    return { view, seo: raw };
  },
  component: RouteComponent,
  head: ({ loaderData }) => headFor((loaderData as any)?.seo, "en"),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Article not found</h1>
      <p className="mt-3 text-muted-foreground">The article you're looking for doesn't exist or was moved.</p>
      <a href="/articles" className="mt-6 inline-flex rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background">Back to articles</a>
    </div>
  ),
});

function RouteComponent() {
  const { view } = Route.useLoaderData();
  return <ArticleView data={view} dict={VIEW_DICT_EN} />;
}

function headFor(seo: any, locale: string) {
  if (!seo || seo.type === "none") return { meta: [{ name: "robots", content: "noindex" }] };
  const canonicalByLocale: Record<string, string> = seo.canonicalPathByLocale ?? {};
  const currentPath = canonicalByLocale[locale] ?? "";
  const canonical = `${SITE_URL}${currentPath}`;

  const alternates = [
    ...Object.entries(canonicalByLocale).map(([loc, p]) => ({ rel: "alternate", hreflang: loc, href: `${SITE_URL}${p}` })),
    { rel: "alternate", hreflang: "x-default", href: `${SITE_URL}${canonicalByLocale.en ?? currentPath}` },
    { rel: "canonical", href: canonical },
  ];

  if (seo.type === "article") {
    const a = seo.article;
    const title = a.seo_title ?? `${a.title} · ${SITE_NAME} Articles`;
    const desc = a.seo_description ?? a.excerpt ?? "Read this Banana Cards article.";
    const image = a.og_image_url ?? a.cover_image_url;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      description: desc,
      image: image ? [image] : undefined,
      author: a.author_name ? { "@type": "Person", name: a.author_name } : undefined,
      publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      datePublished: a.published_at,
      dateModified: a.updated_at ?? a.published_at,
      mainEntityOfPage: canonical,
      inLanguage: locale,
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: a.title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: locale },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: a.title },
        { name: "twitter:description", content: desc },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
        { name: "robots", content: "index,follow" },
      ],
      links: alternates,
      scripts: [{ type: "application/ld+json", children: JSON.stringify(ld) }],
    };
  }

  const c = seo.category;
  const title = c.seo_title ?? `${c.name} · ${SITE_NAME} Articles`;
  const desc = c.seo_description ?? c.description ?? `Articles about ${c.name} from ${SITE_NAME}.`;
  return {
    meta: [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: c.name },
      { property: "og:description", content: desc },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      { property: "og:locale", content: locale },
      { name: "robots", content: "index,follow" },
    ],
    links: alternates,
  };
}